import ActionCard from '@/components/ActionCard'
import { Heading } from '@/components/ui/heading'
import { VStack } from '@/components/ui/vstack'
import useDefaultToast from '@/components/useDefaultToast'
import { isBelowiOS18 } from '@/lib/utils/iosCompat'
import { CurrentUser } from '@/redux/slices/currentUserSlice'
import { createFormThunk } from '@/redux/slices/formSlice'
import { useAppDispatch } from '@/redux/store'
import { Scene, UserRole } from '@/typings/domain'
import { useRouter } from 'expo-router'
import { useState } from 'react'
import { useWindowDimensions, View } from 'react-native'

import EvaluationSVG from '@/assets/images/scenes/evaluation.svg'
import ExamSVG from '@/assets/images/scenes/exam.svg'
import FormSVG from '@/assets/images/scenes/form.svg'
import OnlinePaymentSVG from '@/assets/images/scenes/online_payment.svg'
import RegistrySVG from '@/assets/images/scenes/registry.svg'
import ReservationSVG from '@/assets/images/scenes/reservation.svg'
import SurveySVG from '@/assets/images/scenes/survey.svg'
import { Grid, GridItem } from '@/components/Grid'
import { Icon } from '@/components/ui/icon'
import { MessageSquareTextIcon, SparklesIcon, StarIcon } from 'lucide-react-native'

const CONTENT_PADDING = 32

// Grow columns on wide sheets (iPad) so cards keep roughly phone size
function columnsForWidth(width: number) {
  return Math.max(4, Math.floor((width - CONTENT_PADDING) / 110))
}

type Props = {
  currentUser: CurrentUser
  folderToken?: string
  onDismiss?: () => void
}

export default function CreateForm({ currentUser, folderToken, onDismiss }: Props) {
  const dispatch = useAppDispatch()
  const router = useRouter()
  const toast = useDefaultToast()
  const { width: windowWidth } = useWindowDimensions()
  // iPad sheets are narrower than the window; seed with a capped estimate so the auto-height
  // sheet doesn't first size itself to an oversized layout before onLayout corrects it
  const [cols, setCols] = useState(() => columnsForWidth(Math.min(windowWidth, 700)))

  const handleCreateFormPress = async (scene: Scene) => {
    if (currentUser.role === UserRole.Outworker) {
      toast.show('企业查看员无权创建表单')
      return
    }

    try {
      const form = await dispatch(createFormThunk({ folderToken, scene })).unwrap()
      toast.show('表单创建成功')
      onDismiss?.()
      router.push(`/forms/${form.token}/edit`)
    } catch (error) {
      console.error(error)
      toast.show('表单创建失败')
    }
  }

  const handleAiCreateFormPress = () => {
    if (currentUser.role === UserRole.Outworker) {
      toast.show('企业查看员无权创建表单')
      return
    }

    onDismiss?.()
    router.push('/jiri')
  }

  const handleTemplatePress = () => {
    if (currentUser.role === UserRole.Outworker) {
      toast.show('企业查看员无权创建表单')
      return
    }

    onDismiss?.()
    router.push('/templates')
  }

  const scenes = [
    { scene: Scene.Form, title: '表单', Icon: FormSVG },
    { scene: Scene.Survey, title: '问卷', Icon: SurveySVG },
    { scene: Scene.Registry, title: '报名', Icon: RegistrySVG },
    { scene: Scene.OnlinePayment, title: '收款', Icon: OnlinePaymentSVG },
    { scene: Scene.Reservation, title: '预约', Icon: ReservationSVG },
    { scene: Scene.Exam, title: '考试', Icon: ExamSVG },
    { scene: Scene.Evaluation, title: '测评', Icon: EvaluationSVG },
  ]

  return (
    // onLayout must sit on an RN View: VStack renders a plain <div> on web where it never fires
    <View onLayout={e => setCols(columnsForWidth(e.nativeEvent.layout.width))}>
      <VStack space="lg" className={isBelowiOS18 ? 'p-4 pb-20' : 'p-4'}>
        <VStack space="sm">
          <Heading className="pl-4 text-secondary-label" size="sm">
            手动创建
          </Heading>
          <Grid space="md" cols={cols}>
            {scenes.map(({ scene, title, Icon: SceneIcon }) => (
              <GridItem key={scene}>
                <ActionCard
                  key={scene}
                  title={title}
                  icon={<SceneIcon width={24} />}
                  onPress={() => handleCreateFormPress(scene)}
                />
              </GridItem>
            ))}
          </Grid>
        </VStack>
        <VStack space="sm">
          <Heading className="pl-4 text-secondary-label" size="sm">
            AI 创建
          </Heading>
          <Grid space="md" cols={cols}>
            <GridItem>
              <ActionCard
                title="AI 创建"
                icon={<Icon as={SparklesIcon} size="xl" className="text-purple-500" />}
                onPress={handleAiCreateFormPress}
              />
            </GridItem>
          </Grid>
        </VStack>
        <VStack space="sm">
          <Heading className="pl-4 text-secondary-label" size="sm">
            寻求帮助
          </Heading>
          <Grid space="md" cols={cols}>
            <GridItem>
              <ActionCard
                title="模板"
                icon={<Icon as={StarIcon} size="xl" className="text-orange-500" />}
                onPress={handleTemplatePress}
              />
            </GridItem>
            <GridItem>
              <ActionCard
                title="客服"
                icon={<Icon as={MessageSquareTextIcon} size="xl" className="text-teal-600" />}
                onPress={() => {
                  onDismiss?.()
                  router.push('/profile/customerService')
                }}
              />
            </GridItem>
          </Grid>
        </VStack>
      </VStack>
    </View>
  )
}
